import React, { useMemo } from 'react';
import { Button, List, Space, Tag, Typography } from 'antd';
import {
  RightOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  ExceptionOutlined,
  InboxOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { EmptyQueueState } from '@/components/sales/control-tower/EmptyQueueState';
import type { SupplyChainDashboardActionSummary } from '@/services/supplyChainDashboard.service';

const { Text } = Typography;

type Severity = 'critical' | 'attention' | 'info';

interface AttentionItem {
  id: string;
  severity: Severity;
  title: string;
  reason: string;
  count: number;
  amount?: number;
  action_label: string;
  section: string;
  owner_hint?: string;
}

const severityColor: Record<Severity, string> = {
  critical: 'red',
  attention: 'orange',
  info: 'blue',
};

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const ScmAttentionBrief: React.FC<{
  summary: SupplyChainDashboardActionSummary;
  formatCurrency: (v: number) => string;
  pendingApprovalTotal: number;
  milestonesDue?: number;
}> = ({ summary, formatCurrency, pendingApprovalTotal, milestonesDue = 0 }) => {
  const items = useMemo(() => {
    const out: AttentionItem[] = [];
    const overdue = summary.overdue_po_count ?? 0;
    const dueSoon = summary.due_soon_po_count ?? 0;
    const aging30 = summary.overdue_aging?.['30+'] ?? 0;

    if (overdue > 0) {
      out.push({
        id: 'overdue-pos',
        severity: aging30 > 0 ? 'critical' : 'critical',
        title: 'Overdue purchase orders',
        reason:
          aging30 > 0
            ? `${aging30} aged 30+ days — chase suppliers and confirm revised delivery.`
            : 'Open POs past promised delivery — follow up with suppliers.',
        count: overdue,
        amount: summary.overdue_po_value,
        action_label: 'Open queue',
        section: 'overdue-pos',
        owner_hint: 'Purchasing',
      });
    }
    if (dueSoon > 0) {
      out.push({
        id: 'due-soon',
        severity: 'attention',
        title: 'POs due in next 7 days',
        reason: 'Confirm supplier readiness before these slip into overdue.',
        count: dueSoon,
        amount: summary.due_soon_po_value,
        action_label: 'Review',
        section: 'overdue-pos',
        owner_hint: 'Purchasing',
      });
    }
    if (pendingApprovalTotal > 0) {
      out.push({
        id: 'approvals',
        severity: 'attention',
        title: 'Pending procurement approvals',
        reason: 'PR / bid / PO / GRN / return waiting on approval.',
        count: pendingApprovalTotal,
        action_label: 'Clear queue',
        section: 'pending-approvals',
        owner_hint: 'Approvers',
      });
    }
    if ((summary.prs_pending_sourcing?.length ?? 0) > 0) {
      out.push({
        id: 'sourcing',
        severity: 'attention',
        title: 'PRs pending sourcing',
        reason: 'Approved demand without inquiry / PO — start supplier outreach.',
        count: summary.prs_pending_sourcing.length,
        action_label: 'Source',
        section: 'prs-sourcing',
        owner_hint: 'Buyers',
      });
    }
    if ((summary.grn_not_invoiced?.length ?? 0) > 0) {
      out.push({
        id: 'grn-bills',
        severity: 'info',
        title: 'GRNs ready for supplier bill',
        reason: 'Receipts posted without a matching supplier invoice.',
        count: summary.grn_not_invoiced.length,
        action_label: 'Create bills',
        section: 'grn-not-invoiced',
        owner_hint: 'AP / Procurement',
      });
    }
    if (milestonesDue > 0) {
      out.push({
        id: 'milestones',
        severity: 'attention',
        title: 'PO milestones due',
        reason: 'Advance / stage milestones need acceptance or receipt follow-up.',
        count: milestonesDue,
        action_label: 'Milestones',
        section: 'po-milestones',
        owner_hint: 'Purchasing',
      });
    }
    if ((summary.pos_requiring_advance_count ?? 0) > 0) {
      out.push({
        id: 'advances',
        severity: 'attention',
        title: 'POs requiring advance',
        reason: 'Supplier advance still pending or requested.',
        count: summary.pos_requiring_advance_count ?? 0,
        action_label: 'Review',
        section: 'pending-approvals',
        owner_hint: 'Finance / Purchasing',
      });
    }
    return out;
  }, [summary, pendingApprovalTotal, milestonesDue]);

  const highlights = useMemo(() => {
    const tags: string[] = [];
    if ((summary.overdue_po_count ?? 0) > 0) {
      tags.push(`${summary.overdue_po_count} overdue POs`);
    }
    if ((summary.overdue_aging?.['30+'] ?? 0) > 0) {
      tags.push(`${summary.overdue_aging['30+']} aged 30+`);
    }
    if ((summary.due_soon_po_count ?? 0) > 0) {
      tags.push(`${summary.due_soon_po_count} due soon`);
    }
    if (pendingApprovalTotal > 0) tags.push(`${pendingApprovalTotal} approvals`);
    return tags;
  }, [summary, pendingApprovalTotal]);

  const iconFor = (id: string) => {
    if (id === 'overdue-pos') return <WarningOutlined />;
    if (id === 'due-soon') return <ClockCircleOutlined />;
    if (id === 'approvals') return <ExceptionOutlined />;
    if (id === 'grn-bills') return <InboxOutlined />;
    if (id === 'sourcing') return <FileTextOutlined />;
    return <ThunderboltOutlined />;
  };

  return (
    <ControlTowerSectionCard
      zone="attention"
      sectionId="attention-brief"
      title={
        <Space>
          <ThunderboltOutlined />
          Attention Brief
        </Space>
      }
    >
      {highlights.length > 0 && (
        <Space wrap style={{ marginBottom: 12 }}>
          {highlights.map((h) => (
            <Tag key={h} color="geekblue">
              {h}
            </Tag>
          ))}
        </Space>
      )}
      {!items.length ? (
        <EmptyQueueState message="No urgent procurement delivery or workflow items right now." />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(a) => (
            <List.Item
              actions={[
                <Button
                  key="go"
                  type="link"
                  size="small"
                  icon={<RightOutlined />}
                  onClick={() => scrollTo(a.section)}
                >
                  {a.action_label}
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={iconFor(a.id)}
                title={
                  <Space wrap>
                    <Tag color={severityColor[a.severity]}>{a.severity}</Tag>
                    <Text strong>{a.title}</Text>
                    <Tag>{a.count}</Tag>
                    {a.amount != null ? (
                      <Text type="secondary">{formatCurrency(a.amount)}</Text>
                    ) : null}
                  </Space>
                }
                description={
                  <Text type="secondary">
                    Why: {a.reason}
                    {a.owner_hint ? ` · ${a.owner_hint}` : ''}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </ControlTowerSectionCard>
  );
};

export default ScmAttentionBrief;
